/**
 * App Update Service
 * Checks GitHub Releases for new versions and handles APK download
 */

const GITHUB_OWNER = 'arjunjakka1998';
const GITHUB_REPO = 'FOS-Franchise-Operating-System-Android-V1.0';
const GITHUB_API = `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}/releases/latest`;

// Current app version from package.json (injected at build time)
const CURRENT_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

class AppUpdateService {
  constructor() {
    this.latestRelease = null;
    this.updateAvailable = false;
    this.downloadProgress = 0;
  }

  /**
   * Get current app version
   */
  getCurrentVersion() {
    return CURRENT_VERSION;
  }

  /**
   * Parse version string to comparable number
   * e.g., "1.2.3" -> 1002003
   */
  parseVersion(versionStr) {
    // Extract version number from tag (e.g., "v1.0.0-202402221234" -> "1.0.0")
    const match = versionStr.match(/v?(\d+)\.(\d+)\.(\d+)/);
    if (!match) return 0;
    
    const [, major, minor, patch] = match;
    return parseInt(major) * 1000000 + parseInt(minor) * 1000 + parseInt(patch);
  }

  /**
   * Extract build number from tag
   * e.g., "v1.0.0-202402221234" -> "202402221234"
   */
  parseBuildNumber(tag) {
    const match = tag.match(/-(\d+)$/);
    return match ? match[1] : '0';
  }

  /**
   * Check for updates from GitHub Releases
   */
  async checkForUpdate() {
    try {
      console.log('[AppUpdate] Checking for updates...');
      console.log('[AppUpdate] Current version:', CURRENT_VERSION);
      
      const response = await fetch(GITHUB_API, {
        headers: {
          'Accept': 'application/vnd.github.v3+json',
          'User-Agent': 'FOS-Franchise-App'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('[AppUpdate] No releases found yet');
          return { updateAvailable: false, reason: 'no_releases' };
        }
        throw new Error(`GitHub API error: ${response.status}`);
      }

      const release = await response.json();
      this.latestRelease = release;

      console.log('[AppUpdate] Latest release:', release.tag_name);

      // Compare versions
      const currentVersionNum = this.parseVersion(CURRENT_VERSION);
      const latestVersionNum = this.parseVersion(release.tag_name);
      
      // Also compare build numbers if versions are equal
      const currentBuild = this.parseBuildNumber(`v${CURRENT_VERSION}-0`);
      const latestBuild = this.parseBuildNumber(release.tag_name);

      console.log('[AppUpdate] Current:', currentVersionNum, 'Latest:', latestVersionNum);
      console.log('[AppUpdate] Current Build:', currentBuild, 'Latest Build:', latestBuild);

      // Update is available if version is higher OR same version but newer build
      this.updateAvailable = latestVersionNum > currentVersionNum || 
        (latestVersionNum === currentVersionNum && latestBuild > currentBuild);

      if (this.updateAvailable) {
        // Find the APK asset
        const apkAsset = release.assets.find(asset => asset.name.endsWith('.apk'));
        
        return {
          updateAvailable: true,
          currentVersion: CURRENT_VERSION,
          latestVersion: release.tag_name,
          releaseName: release.name,
          releaseNotes: release.body,
          publishedAt: release.published_at,
          downloadUrl: apkAsset?.browser_download_url || null,
          apkSize: apkAsset?.size || 0,
          apkName: apkAsset?.name || 'FOS-Franchise.apk'
        };
      }

      return {
        updateAvailable: false,
        currentVersion: CURRENT_VERSION,
        latestVersion: release.tag_name,
        reason: 'up_to_date'
      };

    } catch (error) {
      console.error('[AppUpdate] Error checking for updates:', error);
      return {
        updateAvailable: false,
        error: error.message,
        reason: 'error'
      };
    }
  }

  /**
   * Download and install APK update
   * For Capacitor native apps, this triggers the Android package installer
   */
  async downloadAndInstall(downloadUrl, apkName) {
    try {
      console.log('[AppUpdate] Starting download:', downloadUrl);

      // Check if running in Capacitor native environment
      const isNative = window.Capacitor?.isNativePlatform();
      
      if (isNative) {
        // Use Capacitor's native file download and install
        return await this.nativeDownloadAndInstall(downloadUrl, apkName);
      } else {
        // For web/PWA, just open the download URL
        window.open(downloadUrl, '_blank');
        return { success: true, method: 'browser' };
      }
    } catch (error) {
      console.error('[AppUpdate] Download error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Native download and install for Capacitor Android
   */
  async nativeDownloadAndInstall(downloadUrl, apkName) {
    try {
      // Dynamic import to avoid errors on web
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { FileOpener } = await import('@capacitor-community/file-opener');

      console.log('[AppUpdate] Downloading APK...');

      // Download the APK file
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Convert blob to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      });

      // Save to device
      const fileName = apkName || 'FOS-Franchise-update.apk';
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache
      });

      console.log('[AppUpdate] APK saved to:', result.uri);

      // Open with package installer
      await FileOpener.open({
        filePath: result.uri,
        contentType: 'application/vnd.android.package-archive'
      });

      return { success: true, method: 'native', path: result.uri };

    } catch (error) {
      console.error('[AppUpdate] Native install error:', error);
      
      // Fallback to browser download
      window.open(downloadUrl, '_blank');
      return { success: true, method: 'browser_fallback', error: error.message };
    }
  }

  /**
   * Format file size for display
   */
  formatSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format date for display
   */
  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

// Singleton instance
const appUpdateService = new AppUpdateService();
export default appUpdateService;
